import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getSessionStatus } from "../services/faceLiveness";
import debugLogger from "../utils/debug";

/**
 * Hook to poll session status and redirect if cancelled or reset
 * @param {string} sessionId - The session ID to poll
 * @param {boolean} enabled - Whether polling is enabled
 * @param {string} language - Current language for redirection state
 * @param {string} redirectUrl - Redirect URL for redirection state
 */
const useSessionStatusPolling = (sessionId, enabled, language = "en", redirectUrl = null) => {
  const navigate = useNavigate();
  const hasRedirectedRef = useRef(false);

  useEffect(() => {
    if (enabled && sessionId && !hasRedirectedRef.current) {
      const interval = setInterval(async () => {
        try {
          const response = await getSessionStatus(sessionId);
          const currentStatus = response?.data?.status;

          // If status is not_started or cancelled, it means the session was cancelled/reset on the other device
          if (currentStatus === 'not_started') {
            clearInterval(interval);
            if (!hasRedirectedRef.current) {
              hasRedirectedRef.current = true;
              debugLogger.log('Session cancelled/reset detected via polling', { currentStatus });

              navigate("/session-canceled", {
                state: {
                  language,
                  redirect_url: redirectUrl || response?.data?.redirect_url,
                  reason: 'sessionCancelled'
                }
              });
            }
          }
        } catch (err) {
          debugLogger.error('Polling for session status', err);
        }
      }, 3000);

      return () => clearInterval(interval);
    }
  }, [enabled, sessionId, language, redirectUrl, navigate]);

  return { hasRedirected: hasRedirectedRef.current };
};

export default useSessionStatusPolling;
